import RytmWebflow from 'rytm-webflow'
import { gsap } from 'gsap'

/*
 * TypeRevealView — letter-by-letter reveal for big heading typography.
 *
 * Used on headings carrying `data-as-view="typereveal"` (e.g. the item full
 * header title). The heading text is split into per-letter <span>s and animated:
 *
 *   SHOW  1) every letter fades 0 → 10% together, quickly
 *         2) every letter rises 10% → 100% staggered left→right
 *   HIDE  the reverse, faster — 1) every letter falls 100% → 10% staggered
 *         from the end, then 2) the whole block fades 10% → 0 together; the
 *         whole thing fits inside HIDE_TOTAL_S regardless of heading length.
 *
 * Accessibility (WCAG 2.1 AA):
 * - `prefers-reduced-motion: reduce` → no split, no animation; the authored
 *   heading is left untouched and fully visible.
 * - The split heading keeps a single `aria-label` of its full text and every
 *   letter span is `aria-hidden`, so assistive tech reads the whole word/heading
 *   instead of spelling it out letter by letter.
 * - Whitespace stays as text nodes (a soft-wrap opportunity) so words wrap at
 *   spaces but never break mid-word; inline markup (<strong>/<em>) and <br> are
 *   preserved.
 */

const CHAR_CLASS = 'tr-char'

// SHOW timings (seconds), per the subtask spec.
const SHOW_PREFADE_OPACITY = 0.1
const SHOW_PREFADE_DURATION = 0.3
const SHOW_LETTER_DURATION = 0.5
const SHOW_LETTER_STAGGER = 0.02

// HIDE budget (seconds). duration + the full stagger span must fit inside this,
// so both scale down as the heading gets longer.
const HIDE_TOTAL_S = 0.7
const HIDE_STAGGER_MAX = 0.02

class TypeRevealView extends RytmWebflow.WebflowView {

  private tl: gsap.core.Timeline | null = null

  /**
   * Before show — runs against the freshly loaded (still detached) content, so
   * we split + hide the letters here to avoid any full-opacity flash once the
   * content is swapped into the live DOM.
   */
  prepare(container: HTMLElement) {
    super.prepare(container)
    if (this.prefersReducedMotion()) return
    const chars = this.split(container)
    gsap.set(chars, { opacity: 0 })
  }

  /**
   * animate in (show)
   */
  show(container: HTMLElement) {
    super.show(container)
    this.tl?.kill()
    if (this.prefersReducedMotion()) return

    // split() is idempotent — reuses prepare()'s split, or does it now on first
    // page load where prepare() never ran.
    const chars = this.split(container)
    if (!chars.length) return

    // Hide synchronously (covers the initial-load path where prepare() did not).
    gsap.set(chars, { opacity: 0 })

    this.tl = gsap.timeline()
      // 1) every letter ghosts in to 10% together, quickly
      .to(chars, {
        opacity: SHOW_PREFADE_OPACITY,
        duration: SHOW_PREFADE_DURATION,
        ease: 'power2.out',
      })
      // 2) then each letter rises to full, staggered left → right
      .to(chars, {
        opacity: 1,
        duration: SHOW_LETTER_DURATION,
        stagger: SHOW_LETTER_STAGGER,
        ease: 'power2.out',
      })
  }

  /**
   * animate out (hide) — the reverse, faster, fitting inside HIDE_TOTAL_S.
   */
  hide(container: HTMLElement) {
    super.hide(container)
    this.tl?.kill()
    if (this.prefersReducedMotion()) return

    const chars = this.getChars(container)
    if (!chars.length) return

    // Mirror of show, reversed and faster, fitting inside HIDE_TOTAL_S:
    //   1) each letter falls 100% → 10%, staggered from the end
    //   2) the whole block fades 10% → 0 together, fixed at SHOW_PREFADE_DURATION
    // Phase 2 takes a fixed slice of the budget; phase 1 gets the rest. Its
    // stagger is capped (short headings stay staggered) and the per-letter
    // duration takes up the slack (long headings fall near-together).
    const phase1Budget = HIDE_TOTAL_S - SHOW_PREFADE_DURATION
    const lastIndex = Math.max(chars.length - 1, 1)
    const stagger = Math.min(HIDE_STAGGER_MAX, (phase1Budget * 0.5) / lastIndex)
    const duration = phase1Budget - stagger * (chars.length - 1)

    this.tl = gsap.timeline()
      // 1) each letter drops back to the 10% ghost, staggered from the end
      .to(chars, {
        opacity: SHOW_PREFADE_OPACITY,
        duration,
        stagger: { each: stagger, from: 'end' }, // reverse direction of the reveal
        ease: 'power2.in',
      })
      // 2) then the whole block fades out together
      .to(chars, {
        opacity: 0,
        duration: SHOW_PREFADE_DURATION,
        ease: 'power1.in',
      })
  }

  // ---- helpers --------------------------------------------------------------

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * The heading element(s) of this view found within `root` (which may be the
   * live container or, in prepare(), the whole fresh page — hence the
   * `elementBelongsToView` scoping). Falls back to the view container itself
   * when the view is placed directly on a non-heading element.
   */
  private headings(root: HTMLElement): HTMLElement[] {
    const found = [
      ...root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6'),
    ].filter(this.elementBelongsToView)
    if (found.length) return found

    const self = root.matches?.(this.getSelector())
      ? root
      : root.querySelector<HTMLElement>(this.getSelector())
    return self ? [self] : []
  }

  private getSelector(): string {
    return `[data-as-id="${this.id}"]`
  }

  /** All letter spans of this view currently in `root`. */
  private getChars(root: HTMLElement): HTMLElement[] {
    return [...root.querySelectorAll<HTMLElement>('.' + CHAR_CLASS)].filter(
      this.elementBelongsToView
    )
  }

  /**
   * Wrap every non-whitespace character of this view's heading(s) in an inline
   * `<span class="tr-char" aria-hidden="true">`. Idempotent — a heading already
   * split just yields its existing spans.
   */
  private split(root: HTMLElement): HTMLElement[] {
    const chars: HTMLElement[] = []

    for (const heading of this.headings(root)) {
      if (heading.dataset.trSplit) continue

      heading.setAttribute('aria-label', heading.textContent?.trim() ?? '')

      const textNodes: Text[] = []
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT)
      let node: Node | null
      while ((node = walker.nextNode())) textNodes.push(node as Text)

      for (const textNode of textNodes) {
        const text = textNode.nodeValue ?? ''
        if (!text) continue
        const frag = document.createDocumentFragment()
        // Runs of whitespace stay text nodes (soft-wrap opportunities); runs of
        // non-whitespace become one span per character.
        for (const part of text.match(/\s+|\S+/g) ?? []) {
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part))
            continue
          }
          for (const ch of Array.from(part)) {
            const span = document.createElement('span')
            span.className = CHAR_CLASS
            span.setAttribute('aria-hidden', 'true')
            span.textContent = ch
            frag.appendChild(span)
            chars.push(span)
          }
        }
        textNode.parentNode?.replaceChild(frag, textNode)
      }

      heading.dataset.trSplit = '1'
    }

    // Re-split path: collect already-existing spans too.
    return chars.length ? chars : this.getChars(root)
  }
}

export default TypeRevealView
